import { useMemo } from 'react';
import { useClickUpStatuses } from '@/hooks/useClickUp';
import { useWorkspacePMSettings } from '@/hooks/useWorkspacePMSettings';
import { SelectOption } from '../types';

export function useAnalysisOptions(clickupListId?: string | null) {
  const { settings: pmSettings } = useWorkspacePMSettings();
  const { data: clickupStatuses } = useClickUpStatuses(null, clickupListId);

  // Helper to get priority color
  const getPriorityColor = (name: string) => {
    const lower = name.toLowerCase();
    if (lower === 'urgent') return 'text-destructive';
    if (lower === 'high') return 'text-yellow-500';
    if (lower === 'normal' || lower === 'medium') return 'text-blue-500';
    if (lower === 'low') return 'text-muted-foreground';
    return 'text-muted-foreground';
  };

  // Build dynamic priority options from PM settings
  const priorityOptions = useMemo(() => {
    if (!pmSettings?.priorities?.length) {
      return [
        { value: 'urgent', label: 'Urgent', className: 'text-destructive' },
        { value: 'high', label: 'High', className: 'text-yellow-500' },
        { value: 'normal', label: 'Normal', className: 'text-blue-500' },
        { value: 'low', label: 'Low', className: 'text-muted-foreground' },
      ];
    }
    return pmSettings.priorities
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((p) => ({
        value: p.name.toLowerCase(),
        label: p.name,
        className: getPriorityColor(p.name),
      }));
  }, [pmSettings?.priorities]);

  // Build status options directly from ClickUp statuses
  const statusOptions = useMemo(() => {
    if (clickupStatuses && clickupStatuses.length > 0) {
      return clickupStatuses
        .sort((a, b) => a.orderindex - b.orderindex)
        .map((s) => ({
          value: s.name,
          label: s.name,
          className: s.type === 'closed' ? 'text-success' : 'text-primary',
          color: s.color,
        }));
    }

    return [
      { value: 'to do', label: 'To Do', className: 'text-muted-foreground', color: '#808080' },
      { value: 'in progress', label: 'In Progress', className: 'text-blue-500', color: '#4299e1' },
      { value: 'complete', label: 'Complete', className: 'text-success', color: '#6bc950' },
    ];
  }, [clickupStatuses]);

  // Build a map of ClickUp status name -> color for quick lookup
  const clickupStatusColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (clickupStatuses) {
      for (const status of clickupStatuses) {
        map[status.name.toLowerCase()] = status.color;
      }
    }
    return map;
  }, [clickupStatuses]);

  // Helper to get priority class based on value
  const getPriorityClass = (priority: string) => {
    const p = priorityOptions.find((opt) => opt.value === priority?.toLowerCase());
    return p?.className || 'text-muted-foreground';
  };

  // Helper to get status styling based on value
  const getStatusStyle = (status: string): { className: string; color?: string } => {
    const s = statusOptions.find((opt) => opt.value.toLowerCase() === status?.toLowerCase());
    return {
      className: s?.color ? '' : s?.className || 'text-muted-foreground',
      color: s?.color,
    };
  };

  // Helper to find matching status value from statusOptions
  const findStatusValue = (status: string | undefined): string => {
    if (!status) return statusOptions[0]?.value || 'to do';
    const exactMatch = statusOptions.find((opt) => opt.value === status);
    if (exactMatch) return exactMatch.value;
    const caseMatch = statusOptions.find((opt) => opt.value.toLowerCase() === status.toLowerCase());
    if (caseMatch) return caseMatch.value;
    return statusOptions[0]?.value || 'to do';
  };

  return {
    priorityOptions,
    statusOptions,
    clickupStatusColorMap,
    getPriorityClass,
    getStatusStyle,
    findStatusValue,
  };
}

// Health options
export const healthOptions: SelectOption[] = [
  { value: 'green', label: 'Healthy', className: 'text-success' },
  { value: 'yellow', label: 'At Risk', className: 'text-warning' },
  { value: 'red', label: 'Critical', className: 'text-destructive' },
];
